import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Band, Grid as GridDoc, Mark, Row } from '../data';
import { fmtLong, fmtRange, LEADER, leaderShort, outcomeWords, pinnedLeader } from '../format';
import { navigate } from '../router';
import { useSwipe } from '../swipe';
import { cls, COLW, layoutLines, outcomeDay, PHONE, pressable, type PinnedBand, type SortBy } from './Grid';
import { GLYPH } from './marks';

// THE PHONE'S GRID, IN PAGES (Design's phone handoff, design_handoff_phone_view/README.md, Direction A,
// chosen by Dan 29 Sep 2026). The grid is exactly one screen wide and shows PAGE items at a time,
// moved a page at a time by the bottom bar's ‹ › or a sideways swipe: never panned across thousands of
// cells, which dropped 16-50% of frames on a mid-range phone (tools/phone-scroll.mjs, 22 Sep 2026). The
// name column takes whatever the page's cells leave. Each page has a FOCUSED column, lit, whose number,
// date, outcome and whole title the caption strip above the headings gives; tapping the strip opens the
// item. Tablets and desktops keep Grid.tsx, unchanged.
export const PAGE = 8;                   // Dan's pick of the prototype's two (6 was the other)
const CAPTION_H = 60;                    // the caption strip
const HEADS_H = 80;                      // the headings: the number, set vertically, and the outcome rule
export const PHONE_TOP = CAPTION_H + HEADS_H;    // the sticky head; band labels stick beneath it
const MIN_NAME = 120;                    // the name column at 360px, the narrowest phone the design fits
const WINDOW_Y = 800;
const STEP_Y = 200;

export interface PhoneGridProps {
  grid: GridDoc;
  columns: number[];                     // indexes into grid.columns, newest first, filtered
  rowFilter: (row: Row) => boolean;
  openLeft: Record<Band, boolean>;
  onToggleLeft: (band: Band) => void;
  pinned: PinnedBand | null;
  page: number;                          // which page of PAGE items; kept by App, so Back returns to it
  pages: number;
  onPage: (page: number) => void;
  focus: number;                         // the focused column on the page (App: a new page focuses its first)
  onFocus: (i: number) => void;
  onBand: () => void;                    // a band's label: Jump to White House, Senate, House
  sortBy?: SortBy;                       // time in office (the default) or name (Grid.tsx rowCompare)
}

/** The page's width (no scrollbar on a phone; the layout viewport, which minimum-scale=1 holds to the screen). */
const pageWidth = () => document.documentElement.clientWidth || window.innerWidth;

export function PhoneGrid({ grid, columns, rowFilter, openLeft, onToggleLeft, pinned, page, pages, onPage, focus, onFocus, onBand,
                            sortBy = 'tenure' }: PhoneGridProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [vw, setVw] = useState(pageWidth);
  const [viewY, setViewY] = useState(() => ({ y: window.scrollY, h: window.innerHeight }));
  const focusI = focus;
  const lastY = useRef(-1e9);

  const lines = useMemo(() => layoutLines(grid, rowFilter, openLeft, PHONE, pinned, sortBy),
    [grid, rowFilter, openLeft, pinned, sortBy]);
  const pageCols = columns.slice(page * PAGE, page * PAGE + PAGE);
  const empty = PAGE - pageCols.length;                       // a short last page: hatched, never blank
  const nameW = Math.max(MIN_NAME, vw - PAGE * COLW);
  const width = nameW + PAGE * COLW;

  // ---- the page's size, and which rows are near the screen -----------------------------
  useEffect(() => {
    const onScroll = () => {
      if (Math.abs(window.scrollY - lastY.current) < STEP_Y) return;
      lastY.current = window.scrollY;
      setViewY({ y: window.scrollY, h: window.innerHeight });
    };
    const onResize = () => { setVw(pageWidth()); lastY.current = -1e9; onScroll(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize); };
  }, []);

  const [top, setTop] = useState(0);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const t = el.getBoundingClientRect().top + window.scrollY + PHONE_TOP;
    if (Math.abs(t - top) > 1) setTop(t);
  });

  // ---- paging by a sideways swipe (swipe.ts): to the left, older; to the right, newer ------------
  // touch-action: pan-y pinch-zoom (phone.css) leaves scrolling up and down, and zooming, to the browser.
  const pageRef = useRef({ page, pages, onPage });
  pageRef.current = { page, pages, onPage };
  useSwipe(rootRef, (dir) => {
    const { page: p, pages: n, onPage: go } = pageRef.current;
    if (p + dir >= 0 && p + dir < n) go(p + dir);
  });

  // The one shared tooltip, for the page's definitions only (the key's terms, an item's jargon), as
  // Grid.tsx gives them on a desktop: a phone's tap arrives as a mouse move. No crosshair here.
  useEffect(() => {
    const tip = document.getElementById('sr-tip');
    const onMove = (e: MouseEvent) => {
      const def = ((e.target as HTMLElement | null)?.closest?.('[data-def]') as HTMLElement | null)?.dataset.def;
      if (!tip) return;
      if (!def) { tip.style.display = 'none'; return; }
      tip.textContent = def;
      tip.style.display = 'block';
      const w = tip.offsetWidth, h = tip.offsetHeight;
      tip.style.left = Math.max(4, Math.min(e.clientX + 14, document.documentElement.clientWidth - w - 10)) + 'px';
      tip.style.top = (e.clientY - h - 12 < 8 ? e.clientY + 18 : e.clientY - h - 12) + 'px';
    };
    document.addEventListener('mousemove', onMove, true);
    return () => document.removeEventListener('mousemove', onMove, true);
  }, []);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft' && page > 0) { e.preventDefault(); onPage(page - 1); }
    if (e.key === 'ArrowRight' && page < pages - 1) { e.preventDefault(); onPage(page + 1); }
  };
  // a tap on any cell focuses its column (one handler for the page's cells)
  const onTap = (e: MouseEvent) => {
    const c = (e.target as HTMLElement).closest?.('[data-i]') as HTMLElement | null;
    if (c) onFocus(+c.dataset.i!);
  };

  // ---- rows near the screen; the rest are spacers of the same height ---------------------
  const y0 = viewY.y - top - WINDOW_Y, y1 = viewY.y - top + viewY.h + WINDOW_Y;
  type Gap = { kind: 'gap'; key: string; h: number };
  const drawn: (typeof lines.out[number] | Gap)[] = [];
  for (const l of lines.out) {
    if (l.kind === 'member' && !(l.y + l.h > y0 && l.y < y1)) {
      const g = drawn[drawn.length - 1];
      if (g && g.kind === 'gap') g.h += l.h;
      else drawn.push({ kind: 'gap', key: 'gap-' + l.key, h: l.h });
    } else {
      drawn.push(l);
    }
  }

  const col = pageCols.length ? grid.columns[pageCols[Math.min(focusI, pageCols.length - 1)]] : null;
  const day = col ? outcomeDay(col) : null;
  const filler = empty > 0 ? <div class="sr-pempty" style={{ width: empty * COLW + 'px' }} /> : null;

  return (
    <div ref={rootRef} class="sr-grid sr-pgrid" data-sr-grid="1" onKeyDown={onKey}>
      <div ref={contentRef} class="sr-content" style={{ width: width + 'px' }} onClick={onTap}>
        <div class="sr-phead">
          {col && (
            <button class="sr-caption" onClick={() => navigate('/item/' + col.id)}
                    aria-label={`${col.number}${col.title ? ': ' + col.title : ''}. Open the item`}>
              <span class="sr-caption-l1">
                <span class="sr-caption-num">{col.number}</span>
                {day && <span>{fmtLong(day)}</span>}
                <span class={'sr-rule o-' + col.outcome} />
                <span class="sr-caption-outcome">{outcomeWords(col.outcome, col.kind)}</span>
                <span class="sr-caption-open">Open ›</span>
              </span>
              <span class="sr-caption-title">{col.title || col.number}</span>
            </button>
          )}
          <div class="sr-pheads">
            <div class="sr-pcorner" style={{ width: nameW + 'px' }}><span>Member</span><span>Yrs</span></div>
            {pageCols.map((ci, i) => {
              const c = grid.columns[ci];
              return (
                <button key={c.id} class={'sr-phead-cell' + (i === focusI ? ' is-focus' : '')} aria-pressed={i === focusI}
                        aria-label={`${c.number}${c.title ? ': ' + c.title : ''}`} onClick={() => onFocus(i)}>
                  <span class="sr-phead-num">{c.number}</span>
                  <span class={'sr-rule o-' + c.outcome} />
                </button>
              );
            })}
            {filler}
          </div>
        </div>

        {drawn.map((l) => {
          if (l.kind === 'gap') return <div key={l.key} class="sr-gap" style={{ height: l.h + 'px' }} />;
          if (l.kind === 'band') {
            // an anchor where the band starts (its label sticks, so its own place moves) for Jump to
            return [
              <div key={l.key + '-a'} class="sr-band-anchor" data-band-anchor={l.key.slice(2)} />,
              <div key={l.key} class={'sr-band' + (l.pinned ? ' is-pinned' : '')} style={{ top: PHONE_TOP + 'px' }}>
                <button class="sr-band-label sr-band-btn" aria-haspopup="dialog" onClick={onBand}
                        aria-label={`${l.label}, ${l.count}. Jump to another band`}>
                  <span class="sr-band-name">{l.label}</span>
                  <span class="sr-band-count">{l.count}</span>
                </button>
              </div>,
            ];
          }
          if (l.kind === 'toggle') {
            return (
              <button key={l.key} class="sr-ptoggle" aria-expanded={l.open} onClick={() => onToggleLeft(l.band)}>
                <span class="sr-toggle-name">Left office this term</span>
                <span class="sr-toggle-hint">{l.count} {l.open ? '▾' : '▸'}</span>
              </button>
            );
          }
          const line = grid.cells[l.r];
          const row = l.row;
          return (
            <div key={l.key} class={'sr-row' + (l.pinned ? ' is-pinned' : '')
              + (!l.pinned && pinnedLeader(row.leader, row.left) ? ' is-leader' : '')}>
              <PhoneName row={row} left={l.left} width={nameW} reps={!!l.pinned} />
              {pageCols.map((ci, i) => {
                const m = line[ci] as Mark;
                return (
                  <div key={ci} data-r={l.r} data-c={ci} data-i={i}
                       class={'sr-cell c-' + cls(m) + (i === focusI ? ' is-focus' : '')}>{GLYPH[m]}</div>
                );
              })}
              {filler}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** A member's name on the phone grid: the surname, and beneath it the party and seat, the post
 *  ("Speaker · LA-4"), the office ("President"), or the day a member left; the years on the right. */
function PhoneName({ row, left, width, reps }: { row: Row; left: boolean; width: number; reps: boolean }) {
  const sub = left && row.left_on ? `Left ${fmtLong(row.left_on)}`
    : row.role === 'president' ? 'President'
    : row.role === 'vp' ? 'Vice President'
    : row.leader && LEADER[row.leader.role] ? `${leaderShort(row.leader)} · ${row.seat ?? ''}`
    : `${row.party ?? ''} · ${row.seat ?? ''}`;
  return (
    <div class={'sr-name sr-pname' + (left ? ' is-left' : '')} style={{ width: width + 'px' }}
         {...pressable(() => navigate('/member/' + row.id, reps ? { state: { reps: true } } : {}))} aria-label={`${row.full}: member card`}>
      <span class="sr-pname-text">
        <span class="sr-pname-last">{row.last}</span>
        <span class="sr-pname-sub">{sub}</span>
      </span>
      <span class="sr-pname-yrs">{row.years}</span>
    </div>
  );
}

/** The phone's bottom bar (Design's handoff, "Bottom bar"), on the grid, an item and a member: Find, ‹,
 *  where you are (which opens Jump to a month), ›. */
export function Bar({ l1, l2, prev, next, prevLabel, nextLabel, onFind, onMonths }: {
  l1: string; l2: string; prev: (() => void) | null; next: (() => void) | null;
  prevLabel: string; nextLabel: string; onFind: () => void; onMonths: () => void;
}) {
  return (
    <nav class="sr-pbar" aria-label="Move through the record">
      <button class="sr-pbar-find" aria-haspopup="dialog" onClick={onFind}>Find</button>
      <button class="sr-pbar-step sr-pbar-prev" aria-label={prevLabel} disabled={!prev} onClick={() => prev?.()}>‹</button>
      <button class="sr-pbar-mid" aria-haspopup="dialog" onClick={onMonths}>
        <span class="sr-pbar-lines" aria-live="polite">
          <span class="sr-pbar-l1">{l1}</span>
          <span class="sr-pbar-l2">{l2}</span>
        </span>
        <span class="sr-vh">. Jump to a month</span>
      </button>
      <button class="sr-pbar-step sr-pbar-next" aria-label={nextLabel} disabled={!next} onClick={() => next?.()}>›</button>
    </nav>
  );
}

/** The bar on the grid: the middle says which dates and which items the page holds. */
export function PhoneBar({ grid, columns, page, pages, onPage, onFind, onMonths }: {
  grid: GridDoc; columns: number[]; page: number; pages: number; onPage: (p: number) => void;
  onFind: () => void; onMonths: () => void;
}) {
  const on = columns.slice(page * PAGE, page * PAGE + PAGE);
  const dates = on.map((ci) => grid.columns[ci].date).sort();
  const first = page * PAGE + 1, last = page * PAGE + on.length;
  return (
    <Bar l1={dates.length ? fmtRange(dates[0], dates[dates.length - 1]) : 'No items'}
         l2={on.length ? (on.length === 1 ? `Item ${first}` : `Items ${first}–${last}`) + ` of ${columns.length}` : ''}
         prev={page > 0 ? () => onPage(page - 1) : null} next={page < pages - 1 ? () => onPage(page + 1) : null}
         prevLabel="Newer items" nextLabel="Older items" onFind={onFind} onMonths={onMonths} />
  );
}
